/* eslint-disable jira/filename-case */
import mapValues from 'lodash/mapValues';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import type { CollectionView } from '@jira/polaris__domain-collection/src/SrcViewResponse.tsx';
import type { ViewKind } from '@jira/polaris__domain-view/src/ViewKind.types.tsx';
import { experience } from '@jira/polaris__lib-analytics/src/ConstantsExperience.tsx';
import { wrapWithExperience } from '@jira/polaris__lib-analytics/src/createJpdExperience.tsx';
import { createErrorAnalytics } from '@jira/polaris__lib-errors/src/createErrorAnalytics.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { Action } from '@atlassian/react-sweet-state';
import { fetchCollectionsViewsCounts as fetchCollectionsViewsCountsRequest } from './updateCollection.tsx';
import { createView as createViewRequest, deleteView as deleteViewRequest } from './deleteView.tsx';
import type { State } from './State.types.tsx';
import { wrapWithAbortableExperience } from './wrapWithAbortableExperience.tsx';

export type ViewToCreate = {
	visualizationType: ViewKind;
	name: string;
	fields?: string[];
};

export const createView =
	(
		collectionUUID: string,
		view: ViewToCreate,
	): Action<State, void, Promise<CollectionView | undefined>> =>
	async ({ setState, getState }) => {
		try {
			const state = getState();
			const existingCollection = state.collections[collectionUUID];
			const data = existingCollection?.data;

			if (!data) {
				return;
			}

			return await wrapWithAbortableExperience(experience.collections.createView, async () => {
				const newView = await createViewRequest(collectionUUID, view);

				setState({
					collections: {
						...state.collections,
						[collectionUUID]: {
							...existingCollection,
							data: {
								...data,
								views: [...(data?.views || []), newView],
							},
						},
					},
				});

				return newView;
			});
		} catch (error) {
			if (error instanceof Error) {
				fireErrorAnalytics(
					createErrorAnalytics('polaris.error.collectionViewCreationFailed', error),
				);
				throw error;
			}
		}
	};

export const fetchCollectionsViewsCounts =
	(): Action<State, void, Promise<void>> =>
	async ({ setState, getState }) => {
		const state = getState();

		const collectionsUUIDs = Object.keys(state.collections);

		if (collectionsUUIDs.length === 0) {
			return;
		}

		try {
			await wrapWithExperience(experience.collections.loadViewCount, async () => {
				const collectionsViewsCountMap = await fetchCollectionsViewsCountsRequest(collectionsUUIDs);

				setState({
					collections: mapValues(state.collections, (collection) => {
						if (collection?.data) {
							return {
								...collection,
								data: {
									...collection.data,
									viewsCount: collectionsViewsCountMap[collection.data.uuid] || 0,
								},
							};
						}
						return collection;
					}),
				});
			});
		} catch (error) {
			if (error instanceof Error) {
				fireErrorAnalytics(
					createErrorAnalytics('polaris.error.collectionsViewsCountLoadingFailed', error),
				);
			}
			throw error;
		}
	};

export const deleteView =
	(collectionUUID: string, viewUUID: string): Action<State, void, Promise<void>> =>
	async ({ setState, getState }) => {
		try {
			const state = getState();
			const existingCollection = state.collections[collectionUUID];
			const data = existingCollection?.data;

			if (!data) {
				return;
			}

			await wrapWithExperience(experience.collections.deleteView, async () => {
				await deleteViewRequest(collectionUUID, viewUUID);

				setState({
					collections: {
						...state.collections,
						[collectionUUID]: {
							...existingCollection,
							data: {
								...data,
								viewsCount: data.viewsCount ? data.viewsCount - 1 : undefined,
								views: data?.views?.filter((view) => view.uuid !== viewUUID),
							},
						},
					},
				});
			});
		} catch (error) {
			if (error instanceof Error) {
				fireErrorAnalytics(
					createErrorAnalytics('polaris.error.collectionViewDeletionFailed', error),
				);
				throw error;
			}
		}
	};
